import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Alert, Avatar, Chip, Grid, Divider } from '@mui/material';
import { InfoOutlined, PhotoLibrary } from '@mui/icons-material';
import { OutfitRowData, AIAnalysisResponse } from '../../types/admin';
import { ClothingItem, OutfitMetadata } from '../../types';
import uploadService from '../../services/upload-service';
import apiClient from '../../utils/axios-config';
import { useAuth } from '../../contexts/AuthContext';
import BulkDropZone from '../../components/admin/BulkDropZone';
import OutfitDataTable from '../../components/admin/OutfitDataTable';
import BulkUploadButton from '../../components/admin/BulkUploadButton';
import UploadProgressSummary from '../../components/admin/UploadProgressSummary';
import UploadPipelineProgress, { UPLOAD_PIPELINE_STEPS, PipelineStep, PipelineStepStatus } from '../../components/Upload/UploadPipelineProgress';

const API_BASE_URL = 'https://a3d1kmonz2.execute-api.us-east-1.amazonaws.com/dev';

/** Create a fresh set of pipeline steps all set to 'pending' */
const createPendingSteps = (): PipelineStep[] =>
  UPLOAD_PIPELINE_STEPS.map(s => ({ ...s, status: 'pending' as PipelineStepStatus }));

/** Backend step IDs and their estimated durations for animation */
const BACKEND_STEP_IDS = ['moderation', 'resize', 'ai_analysis', 'database', 'indexing'];
const BACKEND_STEP_DURATIONS: Record<string, number> = {
  moderation: 2000, resize: 2000, ai_analysis: 8000, database: 1500, indexing: 1500,
};

const BulkUploadPage: React.FC = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<OutfitRowData[]>([]);
  const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'complete'>('idle');
  const [uploadProgress, setUploadProgress] = useState<{
    completed: number;
    failed: number;
    total: number;
  }>({ completed: 0, failed: 0, total: 0 });
  const [fileRejections, setFileRejections] = useState<
    { file: File; reason: string }[]
  >([]);
  const [validationErrors, setValidationErrors] = useState<Map<string, string[]> | null>(null);

  // Current outfit pipeline progress (shown at the bottom during upload)
  const [currentOutfitIndex, setCurrentOutfitIndex] = useState<number>(-1);
  const [currentPipelineSteps, setCurrentPipelineSteps] = useState<PipelineStep[]>(createPendingSteps());

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      rows.forEach((row) => {
        URL.revokeObjectURL(row.previewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- AI Analysis for newly added rows ---
  const triggerAIAnalysis = useCallback(async (rowId: string, file: File) => {
    // Mark row as loading
    setRows(prev => prev.map(r =>
      r.id === rowId ? { ...r, aiAnalysisStatus: 'loading' as const } : r
    ));

    try {
      // Step 1: Upload image to temp S3 for analysis
      const uploadResponse = await uploadService.getUploadUrl(file.name, file.type, file.size);
      await uploadService.uploadFileToS3(uploadResponse.uploadUrl, file);

      // Step 2: Construct the image URL
      const cloudFrontDomain = process.env.REACT_APP_IMAGES_URL?.replace('https://', '') || 'd34q17au2ltgc3.cloudfront.net';
      const imageUrl = `https://${cloudFrontDomain}/${uploadResponse.key}`;

      // Step 3: Call the admin analyze-outfit endpoint
      const response = await apiClient.post(`${API_BASE_URL}/admin/analyze-outfit`, {
        imageUrl,
      }, { timeout: 60000 });

      const analysisData: AIAnalysisResponse = response.data.data || response.data;

      // Step 4: Map detected clothing items to ClothingItem format
      const clothingItems: ClothingItem[] = (analysisData.detectedClothingItems || []).map(item => ({
        type: item.type as any,
        brand: item.suggestedBrands?.[0] || '',
        color: item.colors?.[0] || '',
        description: '',
        gender: 'Women',
      }));

      // Step 5: Populate row fields with AI results
      setRows(prev => prev.map(r =>
        r.id === rowId ? {
          ...r,
          description: analysisData.description || '',
          weatherTag: analysisData.weatherTags?.[0] || '',
          clothingItems: clothingItems.length > 0 ? clothingItems : r.clothingItems,
          aiAnalysisStatus: 'success' as const,
        } : r
      ));
    } catch (error: any) {
      console.warn(`[BulkUpload] AI analysis failed for row ${rowId}:`, error.message);
      // Mark as failed — fields remain empty and editable
      setRows(prev => prev.map(r =>
        r.id === rowId ? { ...r, aiAnalysisStatus: 'failed' as const } : r
      ));
    }
  }, []);

  // --- BulkDropZone handlers ---
  const handleFilesAccepted = useCallback((files: File[]) => {
    const newRows: OutfitRowData[] = files.map((file) => ({
      id: crypto.randomUUID(),
      file,
      previewUrl: URL.createObjectURL(file),
      eventType: '',
      description: '',
      weatherTag: '',
      clothingItems: [],
      aiAnalysisStatus: 'pending' as const,
      uploadStatus: 'idle' as const,
    }));
    setRows((prev) => [...prev, ...newRows]);
    // Clear previous validation errors when new files are added
    setValidationErrors(null);

    // Note: AI pre-analysis will auto-populate fields when the /admin/analyze-outfit
    // endpoint is deployed. For now, fields are editable manually.
    // The backend upload pipeline still runs full AI analysis during the actual upload.
  }, []);

  const handleFilesRejected = useCallback(
    (rejections: { file: File; reason: string }[]) => {
      setFileRejections((prev) => [...prev, ...rejections]);
    },
    []
  );

  const dismissFileRejection = useCallback((index: number) => {
    setFileRejections((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // --- OutfitDataTable handlers ---
  const handleRowUpdate = useCallback(
    (index: number, updates: Partial<OutfitRowData>) => {
      setRows((prev) =>
        prev.map((row, i) => (i === index ? { ...row, ...updates } : row))
      );
    },
    []
  );

  const handleRowRemove = useCallback((index: number) => {
    setRows((prev) => {
      const row = prev[index];
      if (row) {
        URL.revokeObjectURL(row.previewUrl);
      }
      return prev.filter((_, i) => i !== index);
    });
  }, []);

  const handleClothingItemAdd = useCallback((rowIndex: number) => {
    setRows((prev) =>
      prev.map((row, i) => {
        if (i !== rowIndex) return row;
        const newItem: ClothingItem = {
          type: '' as any,
          brand: '',
          color: '',
          description: '',
          gender: 'Women',
        };
        return { ...row, clothingItems: [...row.clothingItems, newItem] };
      })
    );
  }, []);

  const handleClothingItemRemove = useCallback(
    (rowIndex: number, itemIndex: number) => {
      setRows((prev) =>
        prev.map((row, i) => {
          if (i !== rowIndex) return row;
          return {
            ...row,
            clothingItems: row.clothingItems.filter((_, ci) => ci !== itemIndex),
          };
        })
      );
    },
    []
  );

  const handleClothingItemUpdate = useCallback(
    (rowIndex: number, itemIndex: number, updates: Partial<ClothingItem>) => {
      setRows((prev) =>
        prev.map((row, i) => {
          if (i !== rowIndex) return row;
          const updatedItems = row.clothingItems.map((item, ci) => {
            if (ci !== itemIndex) return item;
            // When gender changes, reset type to empty
            if (updates.gender && updates.gender !== item.gender) {
              return { ...item, ...updates, type: '' as any };
            }
            return { ...item, ...updates };
          });
          return { ...row, clothingItems: updatedItems };
        })
      );
    },
    []
  );

  // --- BulkUploadButton handlers ---
  const handleUpload = useCallback(async () => {
    if (!user) return;

    setUploadState('uploading');
    setUploadProgress({ completed: 0, failed: 0, total: rows.length });
    setValidationErrors(null);

    let completed = 0;
    let failed = 0;

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];

      // Mark row as uploading
      setRows(prev => prev.map((r, idx) =>
        idx === i ? { ...r, uploadStatus: 'uploading' as const, uploadError: undefined } : r
      ));

      // Reset pipeline for this outfit and show it at the bottom
      setCurrentOutfitIndex(i);
      setCurrentPipelineSteps(createPendingSteps());

      // Helper to update a step in the bottom progress section
      const updateStep = (stepId: string, status: PipelineStepStatus) => {
        setCurrentPipelineSteps(prev => prev.map(s => s.id === stepId ? { ...s, status } : s));
      };

      // Animate backend steps on a timer
      let backendTimers: ReturnType<typeof setTimeout>[] = [];
      let backendResolved = false;
      const startBackendAnimation = () => {
        let delay = 0;
        BACKEND_STEP_IDS.forEach((sid, sidx) => {
          const dur = BACKEND_STEP_DURATIONS[sid] || 2000;
          backendTimers.push(setTimeout(() => { if (!backendResolved) updateStep(sid, 'active'); }, delay));
          if (sidx < BACKEND_STEP_IDS.length - 1) {
            backendTimers.push(setTimeout(() => { if (!backendResolved) updateStep(sid, 'done'); }, delay + dur));
          }
          delay += dur;
        });
      };

      // Progress callback from upload service
      const onProgress = (stepId: string) => {
        if (stepId === 'compress') updateStep('compress', 'active');
        else if (stepId === 'upload') { updateStep('compress', 'done'); updateStep('upload', 'active'); }
        else if (stepId === 'backend_start') { updateStep('upload', 'done'); startBackendAnimation(); }
      };

      try {
        // Construct OutfitMetadata from row data
        const metadata: OutfitMetadata = {
          eventType: row.eventType ? [row.eventType] : [],
          clothingItems: row.clothingItems,
          description: row.description,
          tags: row.weatherTag ? [row.weatherTag] : [],
        };

        // Use the existing UploadService to upload the outfit on behalf of the selected user
        await uploadService.uploadOutfitOnBehalf(row.file, metadata, user.id, onProgress);

        // Resolve all steps in the bottom progress
        backendResolved = true;
        backendTimers.forEach(t => clearTimeout(t));
        setCurrentPipelineSteps(prev => prev.map(s => ({ ...s, status: 'done' as PipelineStepStatus })));

        // Mark row as success
        setRows(prev => prev.map((r, idx) =>
          idx === i ? { ...r, uploadStatus: 'success' as const } : r
        ));
      } catch (error: any) {
        failed++;
        backendResolved = true;
        backendTimers.forEach(t => clearTimeout(t));
        // Mark current active step as error in the bottom progress
        setCurrentPipelineSteps(prev => {
          const activeStep = prev.find(s => s.status === 'active');
          const failId = activeStep?.id || 'moderation';
          let pastFail = false;
          return prev.map(s => {
            if (s.id === failId) { pastFail = true; return { ...s, status: 'error' as PipelineStepStatus }; }
            if (pastFail) return { ...s, status: 'pending' as PipelineStepStatus };
            return s;
          });
        });
        // Mark row as failed
        setRows(prev => prev.map((r, idx) =>
          idx === i ? { ...r, uploadStatus: 'failed' as const, uploadError: error.message || 'Upload failed' } : r
        ));
      }

      completed++;
      setUploadProgress({ completed, failed, total: rows.length });
    }

    setUploadState('complete');
    setCurrentOutfitIndex(-1);

    // If all succeeded, clear the table after a short delay
    if (failed === 0) {
      setTimeout(() => {
        setRows(prev => {
          prev.forEach(r => URL.revokeObjectURL(r.previewUrl));
          return [];
        });
        setUploadState('idle');
        setUploadProgress({ completed: 0, failed: 0, total: 0 });
      }, 3000);
    }
  }, [rows, user]);

  const handleValidationErrors = useCallback(
    (errors: Map<string, string[]>) => {
      setValidationErrors(errors);
      // Apply errors to each row's validationErrors field
      setRows((prev) =>
        prev.map((row) => ({
          ...row,
          validationErrors: errors.get(row.id) || undefined,
        }))
      );
    },
    []
  );

  const successCount = rows.filter(r => r.uploadStatus === 'success').length;
  const failedCount = rows.filter(r => r.uploadStatus === 'failed').length;
  const pendingCount = rows.filter(r => r.uploadStatus === 'idle' || r.uploadStatus === 'uploading').length;

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom>Bulk Upload</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Upload multiple outfits at once. Drag and drop images, configure metadata for each outfit, and submit them all in one batch.
      </Typography>

      {!user && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Please log in to use bulk upload.
        </Alert>
      )}

      {/* Top section: two-column grid with user info + upload tips + drop zone */}
      <Grid container spacing={3}>
        {/* Left column: User info + Upload guidelines */}
        <Grid item xs={12} md={4}>
          {/* User card */}
          {user && (
            <Paper sx={{ p: 2.5, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Avatar
                  src={user.profilePicture}
                  alt={user.displayName}
                  sx={{ width: 56, height: 56 }}
                />
                <Box>
                  <Typography variant="subtitle1">{user.displayName}</Typography>
                  <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 1.5 }} />
              <Typography variant="caption" color="text.secondary">
                All outfits will be uploaded to this account.
              </Typography>
            </Paper>
          )}

          {/* Upload Stats */}
          <Paper sx={{ p: 2.5, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <InfoOutlined fontSize="small" color="primary" />
              Upload Summary
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Total outfits</Typography>
                <Chip label={rows.length} size="small" />
              </Box>
              {rows.length > 0 && (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">Ready to upload</Typography>
                    <Chip label={pendingCount} size="small" color="default" />
                  </Box>
                  {successCount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Uploaded</Typography>
                      <Chip label={successCount} size="small" color="success" />
                    </Box>
                  )}
                  {failedCount > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" color="text.secondary">Failed</Typography>
                      <Chip label={failedCount} size="small" color="error" />
                    </Box>
                  )}
                </>
              )}
            </Box>
          </Paper>

          {/* Upload Guidelines */}
          <Paper sx={{ p: 2.5 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhotoLibrary fontSize="small" color="primary" />
              Upload Guidelines
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                • Supported formats: JPEG, PNG, WebP
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Maximum file size: 10 MB per image
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Each outfit needs at least one event type and one clothing item
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • AI will analyze and enrich your outfits during upload
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • You can edit all fields before submitting
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • Failed uploads won't stop the rest of the batch
              </Typography>
            </Box>
          </Paper>
        </Grid>

        {/* Right column: Drop zone + Data table + Actions */}
        <Grid item xs={12} md={8}>
          {/* Drop Zone */}
          <BulkDropZone
            onFilesAccepted={handleFilesAccepted}
            onFilesRejected={handleFilesRejected}
            disabled={uploadState === 'uploading'}
          />

          {/* File rejection errors */}
          {fileRejections.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {fileRejections.map((rejection, index) => (
                <Alert
                  key={`${rejection.file.name}-${index}`}
                  severity="error"
                  sx={{ mb: 1 }}
                  onClose={() => dismissFileRejection(index)}
                >
                  <strong>{rejection.file.name}</strong>: {rejection.reason}
                </Alert>
              ))}
            </Box>
          )}

          {/* Validation error summary */}
          {validationErrors && validationErrors.size > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {validationErrors.size} outfit(s) have validation errors. Please fix them before uploading.
            </Alert>
          )}

          {/* Data Table */}
          <Box sx={{ mt: 3 }}>
            <OutfitDataTable
              rows={rows}
              onRowUpdate={handleRowUpdate}
              onRowRemove={handleRowRemove}
              onClothingItemAdd={handleClothingItemAdd}
              onClothingItemRemove={handleClothingItemRemove}
              onClothingItemUpdate={handleClothingItemUpdate}
            />
          </Box>

          {/* Upload Progress */}
          <UploadProgressSummary
            uploadState={uploadState}
            progress={uploadProgress}
          />

          {/* Pipeline Progress — shows current outfit being processed */}
          {uploadState === 'uploading' && currentOutfitIndex >= 0 && (
            <Paper sx={{ mt: 2, p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Processing Outfit {currentOutfitIndex + 1} of {rows.length}
                </Typography>
                <Chip
                  label={`${uploadProgress.completed} done · ${uploadProgress.failed} failed`}
                  size="small"
                  color={uploadProgress.failed > 0 ? 'warning' : 'primary'}
                  variant="outlined"
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <UploadPipelineProgress steps={currentPipelineSteps} />
              </Box>
            </Paper>
          )}

          {/* Upload Button */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2, alignItems: 'center' }}>
            {rows.length > 0 && uploadState === 'idle' && (
              <Typography variant="body2" color="text.secondary">
                {rows.length} outfit{rows.length !== 1 ? 's' : ''} ready
              </Typography>
            )}
            <BulkUploadButton
              rows={rows}
              hasSelectedUser={!!user}
              isUploading={uploadState === 'uploading'}
              onUpload={handleUpload}
              onValidationErrors={handleValidationErrors}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BulkUploadPage;
